import { MdCheck, MdFavorite, MdSchedule } from 'react-icons/md';
import type { ReactNode } from 'react';

import { cn } from '../../lib/cn';
import { GoogleIcon } from '../icons/google-icon';
import { Modal } from '../modal';
import { PatientAvatar } from './patient-avatar';
import { StarsDisplay } from './stars-display';

export type ReviewDetailView = {
  patientName: string;
  patientEmail: string;
  /** « Il y a 2 jours » : formatted by the caller, who owns the date library. */
  relativeDate: string;
  /** Full date, for the tooltip. */
  fullDate: string;
  /** Overall rating, 1 to 5. */
  rating: number | undefined;
  message: string;
  /** Answers to the extra questions, already filtered to the answered ones. */
  details: { id: string; label: string; type: 'stars' | 'text'; value: number | string }[];
  /** The patient was shown the Google button (rating at or above the threshold). */
  redirected: boolean;
  /** When the patient clicked it; `null` if not. */
  googleClickedAt: string | null;
};

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-2">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-content-subtle">{title}</h3>
      {children}
    </section>
  );
}

/** A small pill of the header: icon + text. */
export function ReviewChip({
  icon,
  title,
  children,
}: {
  icon: ReactNode;
  title?: string;
  children: ReactNode;
}) {
  return (
    <span
      title={title}
      className="inline-flex items-center gap-1.5 rounded-full border border-border-subtle bg-surface px-2.5 py-1 text-xs font-medium text-content-muted"
    >
      <span className="text-sm">{icon}</span>
      {children}
    </span>
  );
}

/**
 * Everything a patient answered: identity and rating up top, then message, extra questions and
 * what happened with Google. Shared by the cabinet's app and the admin console; what differs
 * (extra header pills, the footer's status buttons, the link to the Google page) is passed in.
 */
export function ReviewDetailModal({
  review,
  onClose,
  googleThreshold,
  googleUrl,
  perspective = 'cabinet',
  chips,
  footer,
}: {
  review: ReviewDetailView | null;
  onClose: () => void;
  /** Below this rating, the patient is not sent to Google: the text says so. */
  googleThreshold: number;
  googleUrl?: string | null;
  /** `cabinet`: « votre fiche Google » ; `console` (back-office): « la fiche Google du cabinet ». */
  perspective?: 'cabinet' | 'console';
  /** Pills after the date (practitioner, cabinet…). */
  chips?: ReactNode;
  footer?: ReactNode;
}) {
  const googleSheet = googleUrl ? (
    <a
      href={googleUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="text-brand-600 underline underline-offset-2"
    >
      fiche Google
    </a>
  ) : (
    'fiche Google'
  );
  const ofCabinet = perspective === 'console' ? 'la' : 'votre';
  const owner = perspective === 'console' ? ' du cabinet' : '';
  const clicked = review?.googleClickedAt != null;

  return (
    <Modal open={review !== null} onClose={onClose} size="lg" footer={footer}>
      {review ? (
        <div className="flex flex-col gap-6">
          {/* Header band: bleeds to the modal edges by cancelling the body padding. */}
          <header className="-mx-8 -mt-6 flex flex-col gap-4 border-b border-border-subtle bg-gradient-to-b from-brand-50 to-surface px-8 pb-6 pt-8">
            <div className="flex items-center gap-4 pr-8">
              <PatientAvatar
                name={review.patientName}
                size="lg"
                className="shadow-sm ring-2 ring-white"
              />
              <div className="flex min-w-0 flex-col">
                <h2 className="truncate text-xl font-semibold text-content">
                  {review.patientName || 'Anonyme'}
                </h2>
                {review.patientEmail ? (
                  <span className="truncate text-sm text-content-muted">{review.patientEmail}</span>
                ) : null}
              </div>
            </div>

            {review.rating === undefined ? null : (
              <div className="flex items-center gap-3">
                <StarsDisplay value={review.rating} className="[&_svg]:size-7" />
                <span className="text-2xl font-semibold leading-none text-content">
                  {review.rating}
                  <span className="text-base font-medium text-content-subtle">/5</span>
                </span>
              </div>
            )}

            <div className="flex flex-wrap gap-2">
              <ReviewChip icon={<MdSchedule />} title={review.fullDate}>
                <span className="first-letter:uppercase">{review.relativeDate}</span>
              </ReviewChip>
              {chips}
            </div>
          </header>

          {review.message ? (
            <Section title="Message">
              <p className="whitespace-pre-line break-words rounded-field bg-surface-muted px-4 py-3 leading-relaxed text-content">
                {review.message}
              </p>
            </Section>
          ) : null}

          {review.details.length > 0 ? (
            <Section title="Réponses détaillées">
              <ul className="flex flex-col divide-y divide-border-subtle rounded-field bg-surface-muted">
                {review.details.map((q) => (
                  <li
                    key={q.id}
                    className="flex items-start justify-between gap-6 px-4 py-2.5 text-sm"
                  >
                    <span className="shrink-0 basis-2/5 text-content-muted">{q.label}</span>
                    {q.type === 'stars' ? (
                      <span className="flex shrink-0 items-center gap-2">
                        <StarsDisplay value={Number(q.value)} />
                        <span className="text-xs font-medium text-content-subtle">
                          {Number(q.value)}/5
                        </span>
                      </span>
                    ) : (
                      <span className="min-w-0 whitespace-pre-line break-words text-right text-content">
                        {String(q.value)}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </Section>
          ) : null}

          <Section title="Redirection">
            <div className="flex items-center gap-4 rounded-field bg-surface-muted px-4 py-3">
              <span className="relative grid size-10 shrink-0 place-items-center rounded-full bg-surface text-xl text-brand-600 shadow-sm ring-1 ring-border-subtle">
                {review.redirected ? <GoogleIcon className="size-5" /> : <MdFavorite />}
                {review.redirected ? (
                  <span
                    className={cn(
                      'absolute -bottom-0.5 -right-0.5 grid size-[1.125rem] place-items-center rounded-full text-sm ring-2 ring-surface-muted',
                      clicked
                        ? 'bg-success text-white shadow-[0_0_5px_var(--color-success)]'
                        : 'border border-content-subtle/50 bg-surface text-content-subtle',
                    )}
                  >
                    <MdCheck />
                  </span>
                ) : null}
              </span>
              <div className="flex min-w-0 flex-col gap-0.5 text-sm">
                <span className="font-semibold text-content">
                  {!review.redirected ? (
                    'Patient remercié'
                  ) : clicked ? (
                    <>
                      Patient redirigé vers {ofCabinet} {googleSheet}
                      {owner}
                    </>
                  ) : (
                    <>
                      Patient invité à consulter {ofCabinet} {googleSheet}
                      {owner}
                    </>
                  )}
                </span>
                <span className="leading-relaxed text-content-muted">
                  {!review.redirected
                    ? `Sa note est sous ${googleThreshold} étoiles : Fidalia l’a remercié, sans redirection vers Google. L’avis sera traité par le cabinet dès que possible.`
                    : clicked
                      ? 'Il a cliqué sur le bouton « Laisser un avis Google ».'
                      : 'Mais il n’a pas laissé d’avis Google.'}
                </span>
              </div>
            </div>
          </Section>
        </div>
      ) : null}
    </Modal>
  );
}
